import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { inventoryService } from '../../services/inventoryService';
import { 
  FiArrowLeft, FiPackage, FiTag, FiTruck, FiDollarSign, 
  FiMapPin, FiClock, FiEdit, FiTrash2
} from 'react-icons/fi';

const InventoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [category, setCategory] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await inventoryService.getById(id);
      if (data) {
        setItem(data);
        if (data.categoryId) {
          const cat = await inventoryService.getCategoryById(data.categoryId);
          setCategory(cat);
        }
        if (data.supplierId) {
          const sup = await inventoryService.getSupplierById(data.supplierId);
          setSupplier(sup);
        }
      } else {
        setError('Item tidak ditemukan');
      }
    } catch (error) {
      console.error('Error loading inventory detail:', error);
      setError('Gagal memuat data item');
    } finally {
      setLoading(false);
    }
  };

  const getStockStatus = (item) => {
    const stock = item?.stock || 0;
    const minStock = item?.minStock || 0;
    
    if (stock <= 0) return { label: 'Out of Stock', color: 'bg-red-100 text-red-800' };
    if (stock <= minStock * 0.5) return { label: 'Critical', color: 'bg-red-100 text-red-800' };
    if (stock <= minStock) return { label: 'Low Stock', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'In Stock', color: 'bg-green-100 text-green-800' };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[var(--text-secondary)]">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-600">❌ {error}</p>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[var(--text-secondary)]">Item tidak ditemukan</p>
      </div>
    );
  }

  const stockStatus = getStockStatus(item);

  return (
    <div>
     
      {/* HEADER */}
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => navigate('/inventory')}
          className="p-2 hover:bg-[var(--bg-secondary)] rounded-lg transition-colors"
        >
          <FiArrowLeft size={24} className="text-[var(--text-primary)]" />
        </button>
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Inventory Detail</h1>
        <div className="ml-auto flex space-x-2">
          <button
            onClick={() => navigate(`/inventory/edit/${item.id}`)}
            className="flex items-center space-x-2 px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors text-sm"
          >
            <FiEdit size={16} />
            <span>Edit</span>
          </button>
        </div>
      </div>

      
      {/* MAIN CONTENT */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

      
        {/* KOLOM KIRI (2/3) -ITEM INFO */}
        <div className="lg:col-span-2">
          <div className="bg-[var(--bg-primary)] p-6 rounded-xl shadow-sm border border-[var(--border-color)]">

            {/* --- FOTO&NAMA --- */}
            <div className="flex items-start space-x-4">
              {/* Foto */}
              <div className="flex-shrink-0">
                {item.photo ? (
                  <img
                    src={item.photo}
                    alt={item.name}
                    className="w-24 h-24 object-cover rounded-lg border-2 border-[var(--border-color)]"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-3xl border-2 border-[var(--border-color)]">
                    {item.name?.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>

              {/* Nama &SKU */}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-[var(--text-primary)]">{item.name}</h2>
                    <p className="text-sm text-[var(--text-secondary)] font-mono">{item.sku}</p>
                  </div>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${stockStatus.color}`}>
                    {stockStatus.label}
                  </span>
                </div>
              </div>
            </div>

            {/*Detail Stock &Harga*/}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <p className="text-xs text-[var(--text-secondary)]">Stock</p>
                <p className="text-lg font-semibold text-[var(--text-primary)]">
                  {item.stock} {item.unit}
                </p>
              </div>
              <div>
                <p className="text-xs text-[var(--text-secondary)]">Min Stock</p>
                <p className="text-lg font-semibold text-[var(--text-primary)]">
                  {item.minStock} {item.unit}
                </p>
              </div>
              <div>
                <p className="text-xs text-[var(--text-secondary)]">Buy Price</p>
                <p className="text-lg font-semibold text-[var(--text-primary)]">
                  {formatCurrency(item.buyPrice)}
                </p>
              </div>
              <div>
                <p className="text-xs text-[var(--text-secondary)]">Sell Price</p>
                <p className="text-lg font-semibold text-[var(--text-primary)]">
                  {formatCurrency(item.sellPrice)}
                </p>
              </div>
            </div>

            {/*Deskripsi*/}
            {item.description && (
              <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
                <p className="text-xs text-[var(--text-secondary)]">Description</p>
                <p className="text-sm text-[var(--text-primary)]">{item.description}</p>
              </div>
            )}

            {/*Lokasi*/}
            {item.location && (
              <div className="mt-2 flex items-center space-x-2 text-sm text-[var(--text-secondary)]">
                <FiMapPin size={16} />
                <span>{item.location}</span>
              </div>
            )}

          </div>
        </div>

  
        {/* KOLOM KANAN (1/3) - SIDEBAR */}
        <div className="space-y-4">

          {/* Category */}
          <div className="bg-[var(--bg-primary)] p-4 rounded-xl shadow-sm border border-[var(--border-color)]">
            <h3 className="text-sm font-semibold text-[var(--text-secondary)] mb-2 flex items-center space-x-2">
              <FiTag size={16} />
              <span>Category</span>
            </h3>
            <p className="text-[var(--text-primary)]">{category?.name || 'Tanpa Kategori'}</p>
          </div>

          {/* Supplier */}
          <div className="bg-[var(--bg-primary)] p-4 rounded-xl shadow-sm border border-[var(--border-color)]">
            <h3 className="text-sm font-semibold text-[var(--text-secondary)] mb-2 flex items-center space-x-2">
              <FiTruck size={16} />
              <span>Supplier</span>
            </h3>
            {supplier ? (
              <div>
                <p className="text-[var(--text-primary)]">{supplier.name}</p>
                {supplier.phone && (
                  <p className="text-xs text-[var(--text-secondary)]">📱 {supplier.phone}</p>
                )}
                {supplier.email && (
                  <p className="text-xs text-[var(--text-secondary)]">✉️ {supplier.email}</p>
                )}
              </div>
            ) : (
              <p className="text-[var(--text-secondary)]">Tanpa Supplier</p>
            )}
          </div>

          {/* Status */}
          <div className="bg-[var(--bg-primary)] p-4 rounded-xl shadow-sm border border-[var(--border-color)]">
            <h3 className="text-sm font-semibold text-[var(--text-secondary)] mb-2 flex items-center space-x-2">
              <FiClock size={16} />
              <span>Status</span>
            </h3>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${stockStatus.color}`}>
                {stockStatus.label}
              </span>
              <span className="text-xs text-[var(--text-secondary)]">
                {item.status === 'active' ? '🟢 Active' : '🔴 Inactive'}
              </span>
            </div>
            {item.lastStockUpdate && (
              <p className="text-xs text-[var(--text-secondary)] mt-2">
                Last stock update: {new Date(item.lastStockUpdate).toLocaleString('id-ID')}
              </p>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default InventoryDetail;